import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useController } from '@rest-hooks/react';

import OrganizationResource from 'resources/organization/OrganizationResource';
import ThemeResource from 'resources/organization/ThemeResource';

import SlugEditor from './SlugEditor';
import DetailsEditor from './DetailsEditor';

import createDetailsFormData from './createDetailsFormData';
import createThemeFormData from './createThemeFormData';
import handleValidation from './handleValidation';

import { useError } from 'utils/useErrorController';
import { useToast } from 'utils/context/ToastContext';
import { Box } from '@mui/material';
import ThemeEditor from './ThemeEditor.js';
import FloatingActionButtons from './FloatingActionButtons';

const OrganizationEditor = ({originalOrganization, organization, onOrganizationChange, onPreviewModalOpen, socials, onOrganizationDetailsAndThemeSave, onSocialsEditorSave}) => {

  const [unsavedChanges, setUnsavedChanges] = useState(false);

  const { fetch } = useController();
  const { showToast } = useToast();
  const { setLoading, validate, handleError } = useError();

  const handleDiscardChanges = () => {
    setUnsavedChanges(false);
    onOrganizationChange(originalOrganization);
  };

  const handleOrganizationChange = (newOrganization) => {
    onOrganizationChange(newOrganization);
    setUnsavedChanges(true);
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      validate(organization, handleValidation);

      const formData = createDetailsFormData(organization);
      await fetch(OrganizationResource.update(), { id: organization.id }, formData );

      const { highlight_color, custom_cta, custom_cta_action, theme } = organization;

      if (highlight_color || custom_cta || custom_cta_action || theme) {
        
        const data = {};
        if (highlight_color) data["highlight_color"] = highlight_color;
        if (custom_cta) data["custom_cta"] = custom_cta;
        if (custom_cta_action) data["custom_cta_action"] = custom_cta_action;
        if (theme) data["theme"] = theme;

        const themeFormData = createThemeFormData(data);
        await fetch(ThemeResource.update(), {organization_id: organization.id}, themeFormData);
        //updates draft theme to final
        await fetch(ThemeResource.create(), {organization_id: organization.id}, {});
      }

      onOrganizationDetailsAndThemeSave();
      showToast('success', `Organization Details Updated`);
      setLoading(false);
      setUnsavedChanges(false);

    } catch (error) {
      handleError(error);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

      <SlugEditor 
        organization={organization} 
        onOrganizationChange={handleOrganizationChange} 
        onPreviewModalOpen={onPreviewModalOpen} 
      />

      <DetailsEditor 
        organization={organization}
        onOrganizationChange={handleOrganizationChange}
        socials={socials}
        onSave={onSocialsEditorSave}
      />

      <ThemeEditor 
        organization={organization}
        onOrganizationChange={handleOrganizationChange}
      />

      {unsavedChanges && (
        <FloatingActionButtons
          onSubmit={handleSubmit}
          onDiscard={handleDiscardChanges}
        />
      )}
      
    </Box>
  );
};

OrganizationEditor.propTypes = {
  originalOrganization: PropTypes.object,
  organization: PropTypes.object,
  onOrganizationChange: PropTypes.func,
  onPreviewModalOpen: PropTypes.func,
  socials: PropTypes.array,
  onOrganizationDetailsAndThemeSave: PropTypes.func,
  onSocialsEditorSave: PropTypes.func,
};

export default OrganizationEditor